import { Component, OnInit } from '@angular/core';
import { InsurerService } from 'src/app/core/service/insurer.service';
import { sortBy } from 'lodash';
import { Insurer } from 'src/app/member/model/member';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { Dictionary } from 'src/app/core/model/dictionary';
import { Router } from '@angular/router';
import { PayerBillingService } from 'src/app/core/service/payer-billing.service';
import { GenerateInvoiceRequest } from 'src/app/payer-billing/model/GenerateInvoiceRequest';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { MessengerService, MessageSubject } from 'src/app/core/service/messenger.service';
import moment from 'moment';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-generate-invoice',
  templateUrl: './generate-invoice.component.html',
  styleUrls: ['./generate-invoice.component.scss']
})
export class GenerateInvoiceComponent implements OnInit
{
  public generateForm: any;
  public allPayers: any;
  public payersFiltered: any;
  public defaultPayerItem: Insurer = null;
  public currentDefaultPayerItem: Insurer = null;
  public loaded = false;
  public feeSchedulesFiltered: Dictionary[] = [];
  public allFeeSchedules = [];
  public maxDate: Date = moment().toDate();
  public noPayerFeeScheduleMatch: string;
  public noInvoices: string;
  public invoiceSaved: string;
  public generateInvoiceOverlap: string;

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * Maximum number of items for virtualization. This is to accelerate the DOM performance.
   */
  public virtualFeeSchedule: any = { itemHeight: 30 };

  constructor(
    private insurerService: InsurerService,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private payerBillingService: PayerBillingService,
    private messageHandlerService: MessageHandlerService,
    private messengerService: MessengerService,
    private dictionaryService: DictionaryService,
    private toastrService: ToastrService,)
  {
  }

  public get allPayersCheckbox(): UntypedFormControl
  {
    return this.generateForm.get('allPayersCheckbox');
  }

  public get payers(): UntypedFormControl
  {
    return this.generateForm.get('payers');
  }

  public get feeSchedules(): UntypedFormControl
  {
    return this.generateForm.get('feeSchedules');
  }

  public get fromDateSelection(): UntypedFormControl
  {
    return this.generateForm.get('fromDateSelection');
  }

  public get toDateSelection(): UntypedFormControl
  {
    return this.generateForm.get('toDateSelection');
  }

  public ngOnInit(): void
  {
    this.translate.get(['SELECT-PAYER', 'NO-PAYER-FEE-SCHEDULE-MATCH', 'NO-INVOICE-TO-GENERATE', 'INVOICE-SAVED-SUCCESSFULLY']).subscribe({
      next: (res) =>
      {
        this.defaultPayerItem = { name: res['SELECT-PAYER'], id: null, active: true };
        this.noPayerFeeScheduleMatch = res['NO-PAYER-FEE-SCHEDULE-MATCH'];
        this.noInvoices = res['NO-INVOICE-TO-GENERATE'];
        this.invoiceSaved = res['INVOICE-SAVED-SUCCESSFULLY'];
      }
    });

    this.insurerService.getInsurerList().subscribe({
      next: (data) =>
      {
        this.allPayers = data.filter(payer => payer.feeSchedules.length > 0);
        this.handlePayerFilter('');
      }
    });

    this.buildForm();
  }

  public buildForm(): void
  {
    // Set form
    this.maxDate = moment().toDate();
    this.generateForm = this.fb.group({
      allPayersCheckbox: { value: null, disabled: false },
      payers: [{ value: null, disabled: false }, Validators.required],
      feeSchedules: [{ value: null, disabled: false }, Validators.required],
      fromDateSelection: [{ value: null, disabled: false }, Validators.required],
      toDateSelection: [{ value: null, disabled: false }, Validators.required],
    });
    const allPayers = this.generateForm.controls.allPayersCheckbox.value === undefined || this.generateForm.controls.allPayersCheckbox.value === null ? false : this.generateForm.controls.allPayersCheckbox.value;
    if (allPayers === true)
    {
      this.generateForm.get('payers').disable();
    }
    this.generateForm.get('allPayersCheckbox').valueChanges.subscribe({
      next: (value: boolean) =>
      {
        if (value)
        {
          this.generateForm.get('payers').disable();
          this.generateForm.get('payers').setValue(null);
        }
        else
        {
          this.generateForm.get('payers').enable();
        }
      }
    });
    this.dictionaryService.getCachedFeeScheduless().subscribe({
      next: (data) =>
      {
        this.allFeeSchedules = data;
        this.filterFeeSchedules('');
      }
    });
    this.loaded = true;
  }

  /**
   * Handle Payer filtering.
   *
   * @param filter Filter entered.
   */
  public handlePayerFilter(filter: string): void
  {
    this.searchTerm = filter;
    this.payersFiltered = sortBy(this.allPayers
      .filter((s: Insurer) => s.name.toLowerCase().includes(filter.toLowerCase()) && (s.active || !filter)), ['name']);

    // Setting currentDefaultPayerItem is a workaround to hide the 'Select a payer...' when payer not found.
    this.currentDefaultPayerItem = this.payersFiltered.length === 0 ? null : this.defaultPayerItem;
  }

  public payerItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Filters the Fee SChedules based on user input.
   */
  public filterFeeSchedules(filter: string): void
  {
    this.searchTerm = filter;
    this.feeSchedulesFiltered =
      this.allFeeSchedules.filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
    this.feeSchedules.setValue(null);
  }

  public process()
  {
    let selectedPayers = [];
    if (this.generateForm.controls.allPayersCheckbox.value === true)
    {
      // all payers
      selectedPayers = this.allPayers;
    }
    else
    {
      if (this.generateForm.controls.payers.value !== null && this.generateForm.controls.payers.value.length > 0)
      {
        // selected payers
        selectedPayers = this.generateForm.controls.payers.value;
      }
    }
    const invoiceRequests: GenerateInvoiceRequest[] = [];
    let messages = '';
    selectedPayers.forEach((payer: any) =>
    {
      if (this.feeSchedules.value.filter((g: any) => payer.feeSchedules.some((p: any) => p.id === g.id)).length > 0) // add message for this case
      {
        // Build the generate invoice request
        const generateInvoiceRequest: GenerateInvoiceRequest =
        {
          payerId: payer.id,
          feeSchedules: this.feeSchedules.value.filter((g: any) => payer.feeSchedules.some((p: any) => p.id === g.id)).map((g: any) => g.id).join(','), // only send fee schedules per that payer        
          startDate: this.fromDateSelection.value !== undefined ? moment(this.fromDateSelection.value).format('YYYY-MM-DD').toString() : null,
          endDate: this.toDateSelection.value !== undefined ? moment(this.toDateSelection.value).format('YYYY-MM-DD').toString() : null
        };
        invoiceRequests.push(generateInvoiceRequest);
      }
      else
      {
        const feeSchedules = this.feeSchedules.value.map(x => x.displayName).join(', ');
        const noMatch = this.noPayerFeeScheduleMatch.replace('{{feeSchedules}}', feeSchedules);
        const message = payer.name !== null ? '<b>'.concat(payer.name).concat(':</b><br/>').concat(noMatch) : noMatch;
        messages = messages.concat(message.concat('<br/><br/>'));
      }
    });
    if (invoiceRequests.length > 0)
    {
      this.payerBillingService.generateInvoice(invoiceRequests).subscribe({
        next: (data: any) =>
        {
          if ((data !== null) && (data.length > 0))
          {
            data.forEach(d =>
            {
              messages = messages.concat('<b>').concat(d.payerName).concat(':</b><br/>').concat(this.invoiceSaved).concat('<br/><br/>');
              this.messengerService.sendMessage(new MessageSubject('refreshInvoices'));
            });
            const payerRequest = invoiceRequests.map(x => x.payerId);
            const payerResponse = data.map(x => x.payerId);
            // add "No data found to generate an invoice with these parameters" message for Items in payerRequest but not in payerResponse
            const requestOnly = payerRequest.filter(payerId => !payerResponse.includes(payerId));
            requestOnly.forEach(payerId =>
            {
              messages = messages.concat('<b>').concat(this.allPayers.filter(payer => payer.id === payerId)[0].name).concat(':</b><br/>').concat(this.noInvoices).concat('<br/><br/>');
            });
          }
          else
          {
            messages = messages.concat(this.noInvoices);
          }
          const messageType = (messages.includes(this.invoiceSaved) && !messages.includes(this.noInvoices)) ? 'success' : 'warning';
          this.toastrService[messageType](messages, null,
            {
              enableHtml: true,
              timeOut: 10000,
              messageClass: 'channel-message'
            });
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    else
      if (messages !== '')
      {
        this.toastrService.warning(messages, null,
          {
            enableHtml: true,
            timeOut: 10000,
            messageClass: 'channel-message'
          });
      }
    this.router.navigate(['/payer-billing']);
  }
}
